import React from 'react';
import type { CreativeSlotState } from '../../../types/selfService';
import CreativeScheduleChart from './CreativeScheduleChart';
import {
  applySegmentShareEditWithRecency,
  computeRotationSegments,
  type CreativeWindow,
  type RotationSegment,
} from '../shared/rotationSegments';

interface MultiCreativeSchedulerProps {
  slots: CreativeSlotState[];
  // CTV-457: positional palette — colors[i] is the colour for slots[i].
  colors: string[];
  campaignStartDate: string;
  campaignEndDate: string;
  accentColor: string;
  // CTV-476 follow-up: station slug so the chart can resolve signed preview
  // URLs for each row's thumbnail.
  slug?: string;
  onSlotChange: (slotId: string, patch: Partial<CreativeSlotState>) => void;
  // CTV-476: reset every overlap Rotation Segment to even split. Also
  // clears the per-segment recency stack owned by this component.
  onResetWeights: () => void;
}

function segmentKey(seg: { startDate: string; endDate: string }): string {
  return `${seg.startDate}..${seg.endDate}`;
}

function slotsToWindows(slots: CreativeSlotState[]): CreativeWindow[] {
  return slots.map((s) => ({
    id: s.slotId,
    startDate: s.startDate,
    endDate: s.endDate,
    segmentShares: s.segmentShares.map((seg) => ({ ...seg })),
  }));
}

function sharesEqual(
  a: { startDate: string; endDate: string; share: number }[],
  b: { startDate: string; endDate: string; share: number }[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (
      a[i].startDate !== b[i].startDate ||
      a[i].endDate !== b[i].endDate ||
      a[i].share !== b[i].share
    ) {
      return false;
    }
  }
  return true;
}

/**
 * CTV-457 + CTV-476: rendered in Step 3 when the wizard has ≥2 commercial
 * slots.
 *
 *   1. Chart — unified gantt with monthly-flight guide lines + drag handles.
 *      Each bar segments at overlap boundaries; per-segment share labels
 *      double as inline editors directly on the bar.
 *   2. Date pickers — one labeled row per commercial for keyboard-accessible
 *      editing.
 *   3. Schedule list — read-only Weight column showing the comma-sep
 *      Segment Shares summary (`100% · 50% · 100%`).
 */
const MultiCreativeScheduler: React.FC<MultiCreativeSchedulerProps> = ({
  slots,
  colors,
  campaignStartDate,
  campaignEndDate,
  accentColor,
  slug,
  onSlotChange,
  onResetWeights,
}) => {
  // CTV-476: per-segment recency stack for the inline-input path. Keyed by
  // segmentKey; each value is the ordered list of creativeIds the buyer has
  // edited in that segment (oldest first).
  const [editedOrderBySegment, setEditedOrderBySegment] = React.useState<
    Map<string, string[]>
  >(() => new Map());

  const segments = React.useMemo(
    () =>
      computeRotationSegments(
        slots.map((s) => ({
          id: s.slotId,
          startDate: s.startDate,
          endDate: s.endDate,
          segmentShares: s.segmentShares,
        })),
        campaignStartDate,
        campaignEndDate,
      ),
    [slots, campaignStartDate, campaignEndDate],
  );

  // Prune recency entries for segments that no longer exist (a date change
  // or add/remove can change the segment partition). Without this prune, an
  // ID stuck in a stale stack would silently freeze that creative once the
  // segment re-appears.
  React.useEffect(() => {
    setEditedOrderBySegment((prev) => {
      if (prev.size === 0) return prev;
      const valid = new Set(segments.map(segmentKey));
      let changed = false;
      const next = new Map<string, string[]>();
      for (const [k, v] of prev.entries()) {
        if (valid.has(k)) next.set(k, v);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [segments]);

  // Dispatch a freshly-computed set of CreativeWindows back through the
  // existing onSlotChange callback. We only emit a patch for slots whose
  // segmentShares actually changed (dates are never touched by share edits).
  const dispatchWindowChanges = React.useCallback(
    (updatedWindows: CreativeWindow[]) => {
      for (const w of updatedWindows) {
        const original = slots.find((s) => s.slotId === w.id);
        if (!original) continue;
        if (sharesEqual(original.segmentShares, w.segmentShares)) continue;
        onSlotChange(w.id, { segmentShares: w.segmentShares.map((seg) => ({ ...seg })) });
      }
    },
    [slots, onSlotChange],
  );

  const handleSegmentShareChange = React.useCallback(
    (slotId: string, key: string, value: number) => {
      const seg = segments.find((s) => segmentKey(s) === key);
      if (!seg) return;
      const prevOrder = editedOrderBySegment.get(key) ?? [];
      const result = applySegmentShareEditWithRecency(
        slotsToWindows(slots),
        { startDate: seg.startDate, endDate: seg.endDate },
        slotId,
        value,
        prevOrder,
      );
      dispatchWindowChanges(result.creatives);
      setEditedOrderBySegment((prev) => {
        const next = new Map(prev);
        next.set(key, result.editedOrder);
        return next;
      });
    },
    [segments, slots, editedOrderBySegment, dispatchWindowChanges],
  );

  const handleResetWeights = React.useCallback(() => {
    setEditedOrderBySegment(new Map());
    onResetWeights();
  }, [onResetWeights]);

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-5"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            Rotation & schedule
          </div>
          <div className="text-[14px] font-semibold tracking-tight text-slate-900 mt-0.5">
            Weight and schedule your commercials
          </div>
        </div>
        <button
          type="button"
          onClick={handleResetWeights}
          className="text-[11px] font-medium text-slate-500 hover:text-slate-800 underline underline-offset-2"
        >
          Reset weights to even
        </button>
      </div>

      <CreativeScheduleChart
        slots={slots.map((s) => ({
          slotId: s.slotId,
          filename: s.filename,
          startDate: s.startDate,
          endDate: s.endDate,
          storagePath: s.storagePath,
        }))}
        colors={colors}
        campaignStartDate={campaignStartDate}
        campaignEndDate={campaignEndDate}
        accentColor={accentColor}
        slug={slug}
        onSlotChange={(slotId, patch) => onSlotChange(slotId, patch)}
        segments={segments}
        onSegmentShareChange={handleSegmentShareChange}
      />

      <DatePickerList
        slots={slots}
        colors={colors}
        segments={segments}
        campaignStartDate={campaignStartDate}
        campaignEndDate={campaignEndDate}
        onSlotChange={onSlotChange}
        onSegmentShareChange={handleSegmentShareChange}
      />
    </div>
  );
};

interface DatePickerListProps {
  slots: CreativeSlotState[];
  colors: string[];
  segments: RotationSegment[];
  campaignStartDate: string;
  campaignEndDate: string;
  onSlotChange: (slotId: string, patch: Partial<CreativeSlotState>) => void;
  onSegmentShareChange: (slotId: string, segmentKey: string, value: number) => void;
}

const DatePickerList: React.FC<DatePickerListProps> = ({
  slots,
  colors,
  segments,
  campaignStartDate,
  campaignEndDate,
  onSlotChange,
  onSegmentShareChange,
}) => {
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
        Schedule details
      </div>
      <div className="space-y-2">
        {slots.map((slot, idx) => {
          const color = colors[idx];
          return (
            <div
              key={slot.slotId}
              className="flex items-center gap-3 flex-wrap text-[12px]"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span
                  aria-hidden
                  className="inline-block w-2.5 h-2.5 rounded-full shrink-0 self-start mt-1"
                  style={{ backgroundColor: color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-medium tracking-tight text-slate-700 truncate">
                    {slot.filename || `Commercial ${idx + 1}`}
                  </div>
                  {segments.length > 0 && (
                    <div className="flex items-baseline gap-1.5 text-[11px] tracking-tight tabular-nums mt-0.5">
                      <span className="text-slate-400">Weights</span>
                      <div className="flex items-baseline gap-1">
                        {segments.flatMap((seg, segIdx) => {
                          const eligible = seg.eligible.find((e) => e.creativeId === slot.slotId);
                          const cellKey = `${seg.startDate}..${seg.endDate}`;
                          const cells: React.ReactNode[] = [];
                          if (segIdx > 0) {
                            cells.push(
                              <span key={`${cellKey}-sep`} aria-hidden className="text-slate-300">
                                ·
                              </span>,
                            );
                          }
                          if (eligible) {
                            cells.push(
                              <EditableWeightCell
                                key={cellKey}
                                share={eligible.share}
                                onCommit={(next) => onSegmentShareChange(slot.slotId, cellKey, next)}
                              />,
                            );
                          } else {
                            cells.push(
                              <span
                                key={cellKey}
                                className="text-center shrink-0 text-slate-300"
                                style={{ width: '2.25rem' }}
                              >
                                —
                              </span>,
                            );
                          }
                          return cells;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <SlotDateInputs
                slot={slot}
                campaignStartDate={campaignStartDate}
                campaignEndDate={campaignEndDate}
                onSlotChange={onSlotChange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// CTV-506: commit-on-blur date inputs. Drives the Start / End fields under
// each commercial in the wizard scheduler. onChange writes only to a local
// draft; commit (blur or Enter) clamps to the campaign window, surfaces a
// brief hint, and patches the slot. Escape reverts the draft to the slot's
// canonical value. There is no Start → End auto-bump while typing — End is
// only snapped to Start at commit time if the user produced an inverted
// window.
interface SlotDateInputsProps {
  slot: CreativeSlotState;
  campaignStartDate: string;
  campaignEndDate: string;
  onSlotChange: (slotId: string, patch: Partial<CreativeSlotState>) => void;
}

const SlotDateInputs: React.FC<SlotDateInputsProps> = ({
  slot,
  campaignStartDate,
  campaignEndDate,
  onSlotChange,
}) => {
  const [startDraft, setStartDraft] = React.useState(slot.startDate);
  const [endDraft, setEndDraft] = React.useState(slot.endDate);
  const [startHint, setStartHint] = React.useState<string | null>(null);
  const [endHint, setEndHint] = React.useState<string | null>(null);

  React.useEffect(() => {
    setStartDraft(slot.startDate);
  }, [slot.startDate]);
  React.useEffect(() => {
    setEndDraft(slot.endDate);
  }, [slot.endDate]);

  const clamp = (value: string): string => {
    if (!value) return campaignStartDate;
    if (value < campaignStartDate) return campaignStartDate;
    if (value > campaignEndDate) return campaignEndDate;
    return value;
  };

  const commitStart = () => {
    const isValidYmd = /^\d{4}-\d{2}-\d{2}$/.test(startDraft);
    if (!isValidYmd) {
      setStartDraft(slot.startDate);
      setStartHint(null);
      return;
    }
    const clamped = clamp(startDraft);
    const patch: Partial<CreativeSlotState> = { startDate: clamped };
    if (slot.endDate < clamped) patch.endDate = clamped;
    setStartHint(clamped !== startDraft ? 'Clamped to campaign window' : null);
    setStartDraft(clamped);
    onSlotChange(slot.slotId, patch);
  };

  const commitEnd = () => {
    const isValidYmd = /^\d{4}-\d{2}-\d{2}$/.test(endDraft);
    if (!isValidYmd) {
      setEndDraft(slot.endDate);
      setEndHint(null);
      return;
    }
    const clamped = clamp(endDraft);
    const snapped = clamped < slot.startDate ? slot.startDate : clamped;
    setEndHint(snapped !== endDraft ? 'Clamped to campaign window' : null);
    setEndDraft(snapped);
    onSlotChange(slot.slotId, { endDate: snapped });
  };

  return (
    <>
      <label className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
        Start
        <input
          type="date"
          value={startDraft}
          onChange={(e) => {
            setStartDraft(e.target.value);
            if (startHint) setStartHint(null);
          }}
          onBlur={commitStart}
          onFocus={() => setStartHint(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitStart();
            } else if (e.key === 'Escape') {
              setStartDraft(slot.startDate);
              setStartHint(null);
            }
          }}
          className="block mt-0.5 text-[12px] font-medium tracking-tight text-slate-800 bg-white border border-slate-200 rounded-md px-2 py-1"
        />
        {startHint && (
          <span className="block text-[10px] text-amber-600 mt-0.5 normal-case tracking-normal">
            {startHint}
          </span>
        )}
      </label>
      <span className="text-[12px] text-slate-300">→</span>
      <label className="text-[10px] uppercase tracking-[0.1em] text-slate-400">
        End
        <input
          type="date"
          value={endDraft}
          onChange={(e) => {
            setEndDraft(e.target.value);
            if (endHint) setEndHint(null);
          }}
          onBlur={commitEnd}
          onFocus={() => setEndHint(null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitEnd();
            } else if (e.key === 'Escape') {
              setEndDraft(slot.endDate);
              setEndHint(null);
            }
          }}
          className="block mt-0.5 text-[12px] font-medium tracking-tight text-slate-800 bg-white border border-slate-200 rounded-md px-2 py-1"
        />
        {endHint && (
          <span className="block text-[10px] text-amber-600 mt-0.5 normal-case tracking-normal">
            {endHint}
          </span>
        )}
      </label>
    </>
  );
};

// Editable weight cell rendered in the Schedule details list under each
// creative's filename. Mirrors the chart's InlineShareInput vocabulary
// (dashed underline at rest, solid + tint on hover/focus) but in the
// muted slate palette to fit the subtext context.
interface EditableWeightCellProps {
  share: number;
  onCommit: (next: number) => void;
}

const EditableWeightCell: React.FC<EditableWeightCellProps> = ({ share, onCommit }) => {
  const [editing, setEditing] = React.useState(false);
  const [text, setText] = React.useState<string>('');

  const commit = () => {
    if (!editing) return;
    setEditing(false);
    if (text === '') return;
    const parsed = Number(text);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed !== share) {
      onCommit(parsed);
    }
  };

  // A 100% share means the creative is the only one running in that segment
  // (or has been recency-locked into all of it). Editing it has no meaningful
  // effect since there's no pool to redistribute to, so render plain text.
  if (share === 100) {
    return (
      <span
        className="text-center shrink-0 text-slate-500"
        style={{ width: '2.25rem' }}
      >
        100%
      </span>
    );
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={editing ? text : `${share}%`}
      onFocus={(e) => {
        setEditing(true);
        setText(String(share));
        e.currentTarget.select();
      }}
      onChange={(e) => setText(e.currentTarget.value.replace(/\D/g, ''))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          setEditing(false);
          e.currentTarget.blur();
        }
      }}
      aria-label="Segment weight"
      className="text-center shrink-0 cursor-text bg-transparent text-slate-500 outline-none border-0 border-b border-dashed border-slate-300 hover:border-solid hover:border-slate-500 hover:bg-slate-50 focus:border-solid focus:border-slate-700 focus:bg-slate-50 rounded-sm transition-colors duration-200 ease-out"
      style={{ width: '2.25rem' }}
    />
  );
};

export default MultiCreativeScheduler;
